import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { getErrorObjFormat } from "../../../helpers/errorObjFormat";

const payoutUrl = process.env.REACT_APP_API_BASE_URL + "/v1/payout";
const ordersUrl = process.env.REACT_APP_API_BASE_URL + "/v1/orders";
const condoPayoutUrl = process.env.REACT_APP_API_BASE_URL + "/v1/payout/condos";
const wcPayoutUrl =
  process.env.REACT_APP_API_BASE_URL + "/v1/payout/wc-upgrades";

const initialState = {
  payoutsList: [],
  currentPayout: null,
  hotelOrders: [],
  hotelCommissionPct: null,
  condoOrders: [],
  condoCommissionPct: null,
  isLoading: false,
  isLoadingOrders: false,
  isLoadingCondoOrders: false,
  isCreating: false,
  error: null,
  errorOrders: null,
  errorCondoOrders: null,
  errorCreate: null,
  // WooCommerce upgrade payouts
  wcPayoutsList: [],
  isLoadingWcPayouts: false,
  errorWcPayouts: null,
  wcOrders: [],
  wcProperties: [],
  wcSkippedOrders: [],
  wcCommissionPct: null,
  isLoadingWcOrders: false,
  errorWcOrders: null,
};

const payouts = createSlice({
  name: "payouts",
  initialState,
  reducers: {
    fetchPayoutsStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    fetchPayoutsSuccess(state, { payload }) {
      state.payoutsList = payload;
      state.isLoading = false;
    },
    fetchPayoutsFailure(state, { payload }) {
      state.isLoading = false;
      state.error = payload;
    },

    fetchHotelOrdersStart(state) {
      state.isLoadingOrders = true;
      state.errorOrders = null;
      state.hotelOrders = [];
      state.hotelCommissionPct = null;
    },
    fetchHotelOrdersSuccess(state, { payload }) {
      state.hotelOrders = payload.orders;
      state.hotelCommissionPct = payload.commission_pct;
      state.isLoadingOrders = false;
    },
    fetchHotelOrdersFailure(state, { payload }) {
      state.isLoadingOrders = false;
      state.errorOrders = payload;
    },

    createPayoutStart(state) {
      state.isCreating = true;
      state.errorCreate = null;
    },
    createPayoutSuccess(state) {
      state.isCreating = false;
    },
    createPayoutFailure(state, { payload }) {
      state.isCreating = false;
      state.errorCreate = payload;
    },

    fetchPayoutByIdSuccess(state, { payload }) {
      state.currentPayout = payload;
      state.isLoading = false;
    },

    fetchCondoOrdersStart(state) {
      state.isLoadingCondoOrders = true;
      state.errorCondoOrders = null;
      state.condoOrders = [];
      state.condoCommissionPct = null;
    },
    fetchCondoOrdersSuccess(state, { payload }) {
      state.condoOrders = payload.orders;
      state.condoCommissionPct = payload.commission_pct;
      state.isLoadingCondoOrders = false;
    },
    fetchCondoOrdersFailure(state, { payload }) {
      state.isLoadingCondoOrders = false;
      state.errorCondoOrders = payload;
    },

    clearHotelOrders(state) {
      state.hotelOrders = [];
      state.hotelCommissionPct = null;
      state.errorOrders = null;
    },
    clearCondoOrders(state) {
      state.condoOrders = [];
      state.condoCommissionPct = null;
      state.errorCondoOrders = null;
    },
    clearErrorCreate(state) {
      state.errorCreate = null;
    },

    fetchWcPayoutsStart(state) {
      state.isLoadingWcPayouts = true;
      state.errorWcPayouts = null;
    },
    fetchWcPayoutsSuccess(state, { payload }) {
      state.wcPayoutsList = payload;
      state.isLoadingWcPayouts = false;
    },
    fetchWcPayoutsFailure(state, { payload }) {
      state.isLoadingWcPayouts = false;
      state.errorWcPayouts = payload;
    },

    fetchWcOrdersStart(state) {
      state.isLoadingWcOrders = true;
      state.errorWcOrders = null;
    },
    fetchWcOrdersSuccess(state, { payload }) {
      state.wcOrders = payload.orders;
      state.wcProperties = payload.properties;
      state.wcSkippedOrders = payload.skipped;
      state.wcCommissionPct = payload.commission_pct;
      state.isLoadingWcOrders = false;
    },
    fetchWcOrdersFailure(state, { payload }) {
      state.isLoadingWcOrders = false;
      state.errorWcOrders = payload;
    },
    fetchWcPropertiesSuccess(state, { payload }) {
      state.wcProperties = payload;
    },
  },
});

export const {
  fetchPayoutsStart,
  fetchPayoutsSuccess,
  fetchPayoutsFailure,
  fetchHotelOrdersStart,
  fetchHotelOrdersSuccess,
  fetchHotelOrdersFailure,
  fetchCondoOrdersStart,
  fetchCondoOrdersSuccess,
  fetchCondoOrdersFailure,
  createPayoutStart,
  createPayoutSuccess,
  createPayoutFailure,
  fetchPayoutByIdSuccess,
  clearHotelOrders,
  clearCondoOrders,
  clearErrorCreate,
  fetchWcPayoutsStart,
  fetchWcPayoutsSuccess,
  fetchWcPayoutsFailure,
  fetchWcOrdersStart,
  fetchWcOrdersSuccess,
  fetchWcOrdersFailure,
  fetchWcPropertiesSuccess,
} = payouts.actions;

export default payouts.reducer;

export const fetchPayouts =
  ({ hotelId, condoId, type = "hotel" } = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchPayoutsStart());
      const useCondoUrl = type === "condo" || !!condoId;
      const url = useCondoUrl ? condoPayoutUrl : payoutUrl;
      const params = condoId
        ? { condo_id: condoId }
        : hotelId
        ? { hotel_id: hotelId }
        : {};
      const res = await axios.get(url, {
        params,
        headers: { Authorization: `Bearer ${getState().auth.token}` },
      });
      dispatch(fetchPayoutsSuccess(res.data.response));
    } catch (err) {
      dispatch(fetchPayoutsFailure(getErrorObjFormat(err)));
    }
  };

export const fetchHotelOrders =
  (hotelId, payoutStatus = "unpaid") =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchHotelOrdersStart());
      const res = await axios.get(`${ordersUrl}/hotel/${hotelId}`, {
        params: { payout_status: payoutStatus },
        headers: { Authorization: `Bearer ${getState().auth.token}` },
      });
      dispatch(fetchHotelOrdersSuccess(res.data.response)); // { commission_pct, orders }
    } catch (err) {
      dispatch(fetchHotelOrdersFailure(getErrorObjFormat(err)));
    }
  };

export const createPayout = (payoutObj) => async (dispatch, getState) => {
  try {
    dispatch(createPayoutStart());
    const res = await axios.post(`${payoutUrl}/orders`, payoutObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(createPayoutSuccess());
    return { error: false, data: res.data.response };
  } catch (err) {
    const errorObj = getErrorObjFormat(err);
    dispatch(createPayoutFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

export const fetchCondoOrders =
  (condoId, payoutStatus = "unpaid") =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchCondoOrdersStart());
      const res = await axios.get(`${ordersUrl}/condos/${condoId}`, {
        params: { payout_status: payoutStatus },
        headers: { Authorization: `Bearer ${getState().auth.token}` },
      });
      dispatch(fetchCondoOrdersSuccess(res.data.response));
    } catch (err) {
      dispatch(fetchCondoOrdersFailure(getErrorObjFormat(err)));
    }
  };

export const createCondoPayout = (payoutObj) => async (dispatch, getState) => {
  try {
    dispatch(createPayoutStart());
    const res = await axios.post(`${condoPayoutUrl}/orders`, payoutObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(createPayoutSuccess());
    return { error: false, data: res.data.response };
  } catch (err) {
    const errorObj = getErrorObjFormat(err);
    dispatch(createPayoutFailure(errorObj));
    return { error: true, ...errorObj };
  }
};

export const fetchPayoutById = (payoutId) => async (dispatch, getState) => {
  try {
    dispatch(fetchPayoutsStart());
    const res = await axios.get(`${payoutUrl}/${payoutId}/orders`, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(fetchPayoutByIdSuccess(res.data.response));
    return { error: false, data: res.data.response };
  } catch (err) {
    dispatch(fetchPayoutsFailure(getErrorObjFormat(err)));
    return { error: true };
  }
};

export const fetchWcPayouts =
  ({ accountId } = {}) =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchWcPayoutsStart());
      const res = await axios.get(wcPayoutUrl, {
        params: accountId ? { account_id: accountId } : {},
        headers: { Authorization: `Bearer ${getState().auth.token}` },
      });
      dispatch(fetchWcPayoutsSuccess(res.data.response));
    } catch (err) {
      dispatch(fetchWcPayoutsFailure(getErrorObjFormat(err)));
    }
  };

export const fetchWcProperties = () => async (dispatch, getState) => {
  try {
    const res = await axios.get(`${wcPayoutUrl}/properties`, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(fetchWcPropertiesSuccess(res.data.response));
  } catch (err) {
    // The list only feeds a filter - the page still works without it.
  }
};

// Read live from WooCommerce, so this takes a few seconds.
export const fetchWcOrders = () => async (dispatch, getState) => {
  try {
    dispatch(fetchWcOrdersStart());
    const res = await axios.get(`${wcPayoutUrl}/orders`, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(fetchWcOrdersSuccess(res.data.response)); // { commission_pct, properties, orders, skipped }
  } catch (err) {
    dispatch(fetchWcOrdersFailure(getErrorObjFormat(err)));
  }
};

export const createWcPayout = (payoutObj) => async (dispatch, getState) => {
  try {
    dispatch(createPayoutStart());
    const res = await axios.post(`${wcPayoutUrl}/orders`, payoutObj, {
      headers: { Authorization: `Bearer ${getState().auth.token}` },
    });
    dispatch(createPayoutSuccess());
    return { error: false, data: res.data.response };
  } catch (err) {
    const errorObj = getErrorObjFormat(err);
    dispatch(createPayoutFailure(errorObj));
    return { error: true, ...errorObj };
  }
};
